import { cn } from "@/lib/utils"
import { Wrench, Car, Ship, Bike } from "lucide-react"
import { Link } from "react-router-dom"
import { automotiveContentMap } from "@/data/automotive"

/**
 * Maps subpage titles to their corresponding Lucide icons.
 */
const getIcon = (title: string) => {
  const iconProps = { className: "h-8 w-8" };
  switch (title) {
    case "Service Centers":
      return <Wrench {...iconProps} />;
    case "Automotive Dealers":
      return <Car {...iconProps} />;
    case "Marine":
      return <Ship {...iconProps} />;
    case "Power Sports":
      return <Bike {...iconProps} />;
    default:
      return <Car {...iconProps} />;
  }
};

export function AutomotiveIndustriesSection() {
  const subpages = automotiveContentMap.general.subpages || [];

  return (
    <div className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="reveal reveal-delay-0 text-3xl md:text-4xl font-bold mb-4">Industries We Serve</h2>
          <p className="reveal reveal-delay-1 text-lg text-muted-foreground max-w-2xl mx-auto">
            Specialized solutions for every automotive vertical
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 relative z-10">
          {subpages.map((feature, index) => (
            <Feature
              key={feature.title}
              title={feature.title}
              description={feature.description}
              href={feature.href}
              icon={getIcon(feature.title)}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const Feature = ({
  title,
  description,
  icon,
  index,
  href,
}: {
  title: string
  description: string
  icon: React.ReactNode
  index: number
  href: string
}) => {
  return (
    <Link
      to={href}
      className={cn(
        "reveal reveal-delay-3 flex flex-col lg:border-r py-10 relative group/feature border-border cursor-pointer",
        (index === 0) && "lg:border-l border-border",
        "hover:bg-muted/50 transition-colors duration-200"
      )}
    >
      {/* Hover gradient effect from top */}
      <div className="opacity-0 group-hover/feature:opacity-100 transition duration-200 absolute inset-0 h-full w-full bg-gradient-to-t from-muted/50 to-transparent pointer-events-none" />

      {/* Icon */}
      <div className="mb-4 relative z-10 px-10 text-muted-foreground group-hover/feature:text-[#2F5FE8] transition-colors duration-200">
        {icon}
      </div>

      {/* Title with animated bar */}
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div className="absolute left-0 inset-y-0 h-6 group-hover/feature:h-8 w-1 rounded-tr-full rounded-br-full bg-muted-foreground/30 group-hover/feature:bg-[#2F5FE8] transition-all duration-200 origin-center" />
        <span className="group-hover/feature:translate-x-2 transition duration-200 inline-block text-foreground">
          {title}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground max-w-xs relative z-10 px-10">
        {description}
      </p>
    </Link>
  )
}
